import React from "react";
import compose from "ramda/src/compose";
import pathOr from "ramda/src/pathOr";
import mergeRight from "ramda/src/mergeRight";
import { Controller, useForm } from "react-hook-form";
import Lozenge from "@atlaskit/lozenge";

import { useModalContext } from "../../contexts/ModalContext/context";
import { usePlacementWorksheetSettingsContext } from "../../contexts/PlacementWorksheetContext";
import { Button } from "../../UI/Button";
import { Grid } from "../../UI/Grid";
import { Modal } from "../Modal";
import { getControllerTextFieldRender } from "../../helpers/getControllerTextFieldRender";
import { IPlacementWorksheetUnit } from "../../services/PlacementWorksheetApi";
import { EnhancedTable } from "../EnhancedTable";
import { TableHead } from "../../UI/TableHead";
import { TableRow } from "../../UI/TableRow";
import { TableCell } from "../../UI/TableCell";
import { EnhancedTableBody } from "../EnhancedTableBody";
import { propIdEq } from "../../helpers/propIdEq";
import { useUnitNumberHandler } from "./useUnitNumberHandler";
import { useSnackbar } from "../../hooks/useSnackbar";
import { useInternalCommentsHandler } from "./useInternalCommentsHandler";

interface IFormData {
  unitNumber?: string | null;
  internalComments?: string | null;
}

interface IProps {
  unit: IPlacementWorksheetUnit;
}

export const UnitManageModal: React.FC<IProps> = (props) => {
  const { updateHandler: updateUnitNumber } = useUnitNumberHandler(
    props.unit.unitId,
    false
  );
  const { handleUpdate: updateUnitComment } = useInternalCommentsHandler(
    props.unit.unitId,
    false
  );
  const { labels } = usePlacementWorksheetSettingsContext();
  const [isLoading, setIsLoading] = React.useState(false);
  const { setIsOpen } = useModalContext();
  const { showErrorSnackbar } = useSnackbar();
  const {
    formState: { errors },
    control,
    handleSubmit,
  } = useForm<IFormData>({
    defaultValues: {
      internalComments: props.unit.comment,
      unitNumber: props.unit.unitNumber,
    },
  });

  const closeModal = () => setIsOpen(false);

  const handleSave = async (data: IFormData) => {
    setIsLoading(true);
    try {
      if (data.unitNumber) {
        await updateUnitNumber(data.unitNumber);
      }

      if (data.internalComments) {
        await updateUnitComment(data.internalComments);
      }

      closeModal();
    } catch (error) {
      showErrorSnackbar();
    }
    setIsLoading(false);
  };

  const getDefaultInputProps = mergeRight({ errors });

  const getTextControllerRender = React.useCallback(
    // @ts-ignore
    compose(getControllerTextFieldRender, getDefaultInputProps),
    []
  );

  const getLabelName = compose(pathOr("", ["label"]), (id: number | null) =>
    labels.find(propIdEq(id))
  );

  return (
    <Modal
      maxWidth="sm"
      onClose={closeModal}
      title="Manage Unit"
      actions={
        <>
          <Button
            appearance="subtle"
            onClick={closeModal}
            isDisabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            appearance="primary"
            onClick={handleSubmit(handleSave)}
            isLoading={isLoading}
          >
            Save
          </Button>
        </>
      }
    >
      <Grid container spacing={2} p={3}>
        <Grid item xs={12}>
          <Controller
            name="unitNumber"
            control={control}
            // @ts-ignore
            render={getTextControllerRender({ label: "Unit #" })}
          />
        </Grid>
        <Grid item xs={12}>
          <EnhancedTable title="Residents">
            <TableHead secondary>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Bedroom</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Gender</TableCell>
              </TableRow>
            </TableHead>
            <EnhancedTableBody colSpan={4}>
              {props.unit.occupants.map((occupant) => (
                <TableRow key={occupant.id}>
                  <TableCell>
                    {occupant.firstName} {occupant.lastName}
                  </TableCell>
                  <TableCell>{getLabelName(occupant.roomLabelId)}</TableCell>
                  <TableCell>
                    <Lozenge
                      appearance={occupant.status === "new" ? "new" : "moved"}
                    >
                      {occupant.status}
                    </Lozenge>
                  </TableCell>
                  <TableCell>{occupant.gender}</TableCell>
                </TableRow>
              ))}
            </EnhancedTableBody>
          </EnhancedTable>
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="internalComments"
            control={control}
            // @ts-ignore
            render={getTextControllerRender({ label: "Internal Comments" })}
          />
        </Grid>
      </Grid>
    </Modal>
  );
};
